/* eslint-disable react/no-unstable-nested-components */
import * as React from "react";
import {
    SearchInput,
    Popover,
    PopoverTrigger,
    PopoverContent,
    Button,
    Dropdown,
    DropdownTrigger,
    DropdownContent,
    DropdownItem,
    Checkbox,
    DatePicker,
    Table,
    Pagination,
    Dialog,
    DialogContent,
    FilterButton,
    Card,
    ActionButton,
    Badge,
} from "@jhool-io/fe-components";
import { useSearchParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { ColumnDef, ExpandedState, Row } from "@tanstack/react-table";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";

import Navbar from "../../../../components/Navbar/Navbar";
import { CPT_CODES, LISTS_DEFAULT_LIMIT } from "../../../../utils/constants";
import { useDebounce } from "../../../../hooks/helpers";
import {
    buildSuccessMessage,
    cn,
    convertDateFilterStringToDate,
    displayNameInRightFormat,
    formatDate,
    getTotalAmountOfTableRowsSelected,
    removeEnumUnderscore,
    showMoneyInAppFormat,
    truncateString,
} from "../../../../utils/helpers";
import ListState from "../../../../components/ListState/ListState";

import {
    IBillingResponse,
    IUpdatedInvoiceResponse,
} from "../../../../utils/types/billing";
import { useUpdateInvoiceStatus } from "../../../../hooks/mutations/billing";
import { IPaginatedApiResponse } from "../../../../utils/types/api-response";
import useToast from "../../../../hooks/useToast";
import {
    BillPaymentStatus,
    BillStatus,
    BillStatusesWithIssueDetails,
} from "../../types/billing.types";
import {
    getBillPaymentStatusTag,
    getBillStatusFilterLabel,
    getBillStatusLabel,
    getBillStatusTag,
} from "../../helpers/billing.helpers";
import useShowInsuranceOverSelfPay from "../../../practice-settings/hooks/useShowInsuranceOverSelfPay";
import TableFiltersDisplay from "../../../../shared-ui/TableFiltersDisplay/TableFiltersDisplay";
import { useFetchInvoices } from "../../hooks/queries/billing.queries";
import PendingResolutionForm, {
    PendingResolutionMetaData,
} from "../PendingResolutionForm/PendingResolutionForm";
import { useFetchProviders } from "../../../providers/hooks/providers.queries";
import styles from "./BillsList.module.scss";
import Loader from "../../../../shared-ui/Loader/Loader";
import InsuranceProviderFilter from "../../../../shared-ui/InsuranceProviderFilter/InsuranceProviderFilter";
import InfoIcon from "../../../../icons/Info";
import {
    usePendingResolutionDialogText,
    useSortedBillStatuses,
} from "../../hooks/helpers.hooks";
import { getStatusTag } from "../../../claims/helpers/claims.helper";
import { getCptCodeToDisplay } from "../../../notes/helpers/notes.helpers";
import { NoteLabels, NoteTypes } from "../../../notes/types/notes.types";

type ComboOption = {
    value: string;
    label: string;
};

const PendingResolutionOptions = [
    "card_payment_failed",
    "card_payment_failed_pr100",
    "no_matched_payment",
    "other",
];

export default function BillsList() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const { toast } = useToast();

    const getBillStatus = () => {
        const invoiceStatusFilter = searchParams.getAll("invoice_status[]");

        if (invoiceStatusFilter.length > 0) {
            if (invoiceStatusFilter.includes("all")) {
                return [];
            }
            return invoiceStatusFilter;
        }

        return [];
    };

    const pendingResolutionDialogText = usePendingResolutionDialogText();

    const invoiceStatusFilter = getBillStatus() || [];
    const cptCodeFilter = searchParams.get("cpt_code") || "";
    const pendingResolutionFilter =
        searchParams.get("pending_resolution") || "";
    const fromDateFilter = searchParams.get("from_date");
    const toDateFilter = searchParams.get("to_date");
    const clientFilter = searchParams.get("search_string") || "";
    const pageFilter = Number(searchParams.get("page")) || 1;
    const limitFilter =
        Number(searchParams.get("limit")) || LISTS_DEFAULT_LIMIT;
    const showArchivedNotesFilter = searchParams.get("show_archived") || "";
    const [rowSelection, setRowSelection] = React.useState({});
    const [getSelectedData, setGetSelectedData] =
        React.useState<Row<IBillingResponse>[]>();
    const [propsForPendingResolution, setPropsForPendingResolution] =
        React.useState<PendingResolutionMetaData | null>(null);
    const [showPendingReolutionDialog, setShowPendingResolutionDialog] =
        React.useState(false);
    const [insuranceProviderIdsFilter, setInsuranceProviderIdsFilter] =
        React.useState<string[]>([]);
    const [sortConfig, setSortConfig] = React.useState<{
        key: string;
        direction: string;
    }>({
        key: "",
        direction: "",
    });
    const [showCliniciansListPopover, setShowCliniciansListPopover] =
        React.useState(false);
    const [selectedProvider, setSelectedProvider] =
        React.useState<ComboOption | null>(null);
    const [providerSearchString, setProviderSearchString] = React.useState("");

    const fromDateFilterToDate = convertDateFilterStringToDate(fromDateFilter);
    const toDateFilterToDate = convertDateFilterStringToDate(toDateFilter);
    const debouncedClientFilter = useDebounce(clientFilter);

    const debouncedProviderSearchValue = useDebounce(providerSearchString, 500);

    const showInsOverSelfPay = useShowInsuranceOverSelfPay();

    const sortableColumns = [
        "client_name",
        "provider_name",
        "coinsurance_charge",
        "coinsurance_amount_collected",
        "insurance_amount_collected",
        "insurance_charge",
        "insurance_provider",
        "cpt_code",
        "date_of_service",
        "note_author",
    ];

    const providerList = useFetchProviders({
        page: 1,
        search_string: debouncedProviderSearchValue.toLowerCase(),
        limit: 20,
    });

    const handleConfigAttribute = () => {
        if (sortableColumns.includes(sortConfig.key)) {
            return sortConfig.key;
        }

        return undefined;
    };

    const sortAttribute =
        localStorage.getItem("sort_attr_bills") ||
        searchParams.get("sort_attr") ||
        handleConfigAttribute();
    const sortOrder =
        localStorage.getItem("sort_order_bills") ||
        searchParams.get("sort_order") ||
        sortConfig.direction;

    const { data, isLoading, error, isSuccess } = useFetchInvoices({
        search_string: debouncedClientFilter,
        insurance_provider_ids: insuranceProviderIdsFilter,
        invoice_status: invoiceStatusFilter,
        cpt_code: cptCodeFilter,
        pending_resolution_category: pendingResolutionFilter,
        from_date: fromDateFilter,
        to_date: toDateFilter,
        page: pageFilter,
        limit: limitFilter,
        sort_attribute: sortAttribute,
        sort_order: sortOrder,
        author_id: selectedProvider?.value,
        show_archived: showArchivedNotesFilter,
    });

    const patchInvoiceStatus = useUpdateInvoiceStatus();

    const queryClient = useQueryClient();

    const updateInVoiceIsMutating = useIsMutating(["update-invoice"]);

    const handleRowClick = (row: Row<IBillingResponse>) => {
        const navState = new URLSearchParams(searchParams);
        if (navState.getAll("invoice_status[]").length === 0) {
            navState.append("invoice_status[]", "all");
        }
        insuranceProviderIdsFilter.forEach((insuranceProviderId) =>
            navState.append("insurance_provider_ids[]", insuranceProviderId)
        );
        if (selectedProvider?.value) {
            navState.set("author_id", selectedProvider.value);
        }
        if (sortAttribute) {
            navState.set("sort_attribute", sortAttribute);
        }
        if (sortOrder) {
            navState.set("sort_order", sortOrder);
        }
        navState.set("page", "1");
        navigate(
            `/billing-and-claims/${row.original.client.client_id}/${row.original.note_id}`,
            {
                state: navState.toString(),
            }
        );
    };

    const handleRowStyle = (row: Row<IBillingResponse>) => {
        if (
            row.original.insurance_amount > 0 &&
            row.original.accounting_insurance < 0
        ) {
            return {
                backgroundColor: "#ffebed",
                borderBottom: "1px solid #ECEEF2",
            };
        }
        if (
            row.original.insurance_amount === 0 &&
            row.original.accounting_coinsurance < 0
        ) {
            return {
                backgroundColor: "#ffebed",
                borderBottom: "1px solid #ECEEF2",
            };
        }
        return {
            backgroundColor: "var(--bg-table)",
            borderBottom: "1px solid #ECEEF2",
        };
    };

    const handleStatusChange = (status: string) => {
        if (invoiceStatusFilter.includes(status)) {
            searchParams.delete("invoice_status[]", status);
        } else {
            searchParams.append("invoice_status[]", status);
        }
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleCptCodeChange = (value: string) => {
        if (value === "") searchParams.delete("cpt_code");
        else searchParams.set("cpt_code", value);

        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handlePendingResolutionChange = (value: string) => {
        if (value === "") searchParams.delete("pending_resolution");
        else searchParams.set("pending_resolution", value);

        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleFromDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("from_date");
        else searchParams.set("from_date", format(date, "yyyy-MM-dd"));

        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleToDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("to_date");
        else searchParams.set("to_date", format(date, "yyyy-MM-dd"));

        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleClientFilterChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        if (e.target.value === "") searchParams.delete("search_string");
        else searchParams.set("search_string", e.target.value);

        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const getArchivedNotesFilterToDisplay = () => {
        if (showArchivedNotesFilter === "true") {
            return "Archived";
        }
        if (showArchivedNotesFilter === "false") {
            return "Unarchived";
        }

        return "Archive status";
    };

    const handleSetArchivedNotesFilter = (archived: string) => {
        if (archived === "") searchParams.delete("show_archived");
        else searchParams.set("show_archived", archived);
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handlePageChange = (page: number) => {
        searchParams.set("page", String(page));
        setSearchParams(searchParams);
    };

    const handleLimitChange = (limit: number) => {
        searchParams.set("limit", String(limit));
        setSearchParams(searchParams);
    };

    const handleShowPendingResolutionDialog = (row?: Row<IBillingResponse>) => {
        setShowPendingResolutionDialog(true);
        setPropsForPendingResolution({
            noteId: row?.original.note_id,
            clientId: row?.original.client.client_id,
            invoiceId: row?.original.invoice_id,
            invoiceIds: getSelectedData
                ? getSelectedData.map((item) => item.original.invoice_id)
                : undefined,
        });
    };

    const handleClosePendingResolutionDialog = () => {
        setShowPendingResolutionDialog(false);
        setPropsForPendingResolution(null);
        searchParams.delete("payment_failed");
        setSearchParams(searchParams);
    };

    const sortedBillStatuses = useSortedBillStatuses();

    const handleChangeStatus = (status: BillStatus) => {
        if (getSelectedData) {
            const selectedIds = getSelectedData.map(
                (item: Row<IBillingResponse>) => item.original.invoice_id
            ) as string[];
            const changeStatusPayload = {
                invoice_ids: selectedIds,
                status,
            };
            patchInvoiceStatus.mutate(changeStatusPayload, {
                onSuccess: async (response) => {
                    queryClient.setQueryData<
                        IPaginatedApiResponse<IUpdatedInvoiceResponse>
                    >(["invoices"], (prev) => {
                        const prevRequired =
                            prev as IPaginatedApiResponse<IUpdatedInvoiceResponse>;
                        return {
                            ...prevRequired,
                            invoice_status: response.status,
                        };
                    });
                    await queryClient.invalidateQueries({
                        queryKey: ["invoices"],
                    });
                    toast({
                        mode: "success",
                        message: `${buildSuccessMessage(
                            response.data.updated_invoices,
                            response.data.unupdated_invoices
                        )}`,
                    });
                    setRowSelection({});
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not update selected bills at this time",
                    });
                },
            });
        }
    };

    const handleBillStatusSelect = (status: BillStatus) => {
        if (status === BillStatus.PENDING_RESOLUTION) {
            handleShowPendingResolutionDialog();
            return;
        }
        handleChangeStatus(status);
    };

    const invoiceItems =
        (data &&
            data.data.filter(
                (item) =>
                    item.note_type !== "supervision_note" &&
                    item.note_type !== "safety_plan"
            )) ||
        [];

    const cptCodeSectionDisplay = (
        row: Row<IBillingResponse>,
        hasDifferentBackground: boolean
    ) => {
        return (
            <span
                className={
                    hasDifferentBackground ? "text-[#0b132b]" : "text-text"
                }
            >
                {getCptCodeToDisplay({
                    noteType: row.original.note_type as NoteTypes,
                    cptCode: row.original.cpt_code,
                    unitCalculation:
                        row.original.note_metadata?.unit_calculation,
                })}
            </span>
        );
    };

    const columns: ColumnDef<IBillingResponse>[] = React.useMemo(() => {
        const hasDifferentBackground = (row: Row<IBillingResponse>) => {
            return (
                (row.original.insurance_amount > 0 &&
                    row.original.accounting_insurance < 0) ||
                (row.original.insurance_amount === 0 &&
                    row.original.accounting_coinsurance < 0)
            );
        };
        return [
            {
                id: "select",
                header: ({ table }) => (
                    <Checkbox
                        handleChange={table.getToggleAllRowsSelectedHandler()}
                        indeterminate={table.getIsSomeRowsSelected()}
                        label=""
                        name="row"
                        value="row"
                        isChecked={table.getIsAllRowsSelected()}
                        labelClass="z-0"
                    />
                ),
                cell: ({ row }) => (
                    <Checkbox
                        handleChange={row.getToggleSelectedHandler()}
                        indeterminate={row.getIsSomeSelected()}
                        id={row.original.invoice_id}
                        label=""
                        name="row"
                        value="row"
                        isChecked={row.getIsSelected()}
                        labelClass="z-0"
                    />
                ),
            },
            {
                header: " ",
                columns: [
                    {
                        accessorKey: "client_name",
                        header: "CLIENT",
                        cell: ({ row }) => (
                            <div className="flex flex-col gap-y-1">
                                <span
                                    className={cn("text-text", {
                                        "text-[#0b132b]":
                                            hasDifferentBackground(row),
                                    })}
                                >
                                    {row.original.client
                                        ? displayNameInRightFormat({
                                              firstName:
                                                  row.original.client
                                                      .first_name,
                                              lastName:
                                                  row.original.client.last_name,
                                          })
                                        : "--"}
                                </span>
                                <span
                                    className={
                                        hasDifferentBackground(row)
                                            ? "text-[#0b132b]"
                                            : "text-text"
                                    }
                                >
                                    {`Bill: `}
                                    {getBillStatusTag(
                                        row.original
                                            .invoice_status as BillStatus
                                    )}
                                </span>
                            </div>
                        ),
                    },
                ],
            },
            {
                header: " ",
                columns: [
                    {
                        accessorKey: "date_of_service",
                        header: "DATE OF SERVICE",
                        cell: ({ row }) => (
                            <div className="flex flex-col gap-y-1">
                                <span
                                    className={
                                        hasDifferentBackground(row)
                                            ? "text-[#0b132b]"
                                            : "text-text"
                                    }
                                >
                                    {formatDate(row.original.date_of_service) ||
                                        "--"}
                                </span>
                                {row.original.is_note_archived ? (
                                    <Badge
                                        type="info"
                                        content="Archived"
                                        className="self-start"
                                    />
                                ) : null}
                            </div>
                        ),
                    },
                ],
            },
            {
                header: " ",
                columns: [
                    {
                        accessorKey: "cpt_code",
                        header: "CPT CODE",
                        cell: ({ row }) => {
                            return (
                                <div
                                    className={cn(
                                        "flex flex-col items-start gap-y-1"
                                    )}
                                >
                                    {cptCodeSectionDisplay(
                                        row,
                                        hasDifferentBackground(row)
                                    )}
                                    <span
                                        className={
                                            hasDifferentBackground(row)
                                                ? "text-[#0b132b]"
                                                : "text-text"
                                        }
                                    >
                                        {`Claim: `}
                                        {row.original.active_claim_status
                                            ? getStatusTag(
                                                  row.original
                                                      .active_claim_status
                                              )
                                            : "--"}
                                    </span>
                                </div>
                            );
                        },
                    },
                ],
            },
            {
                header: " ",
                columns: [
                    {
                        accessorKey: "pos_code",
                        header: "pos code",
                        cell: ({ row }) => {
                            const regex = /^(\d+) - (.+)$/;

                            if (row.original.pos_code) {
                                return (
                                    <ActionButton
                                        variant="normal"
                                        size="auto"
                                        iconRight={<InfoIcon />}
                                        actionTitle={
                                            row.original.pos_code.match(
                                                regex
                                            )?.[2] || ""
                                        }
                                        className={
                                            hasDifferentBackground(row)
                                                ? "text-[#0b132b]"
                                                : "text-text"
                                        }
                                    >
                                        {row.original.pos_code.match(
                                            regex
                                        )?.[1] || ""}
                                    </ActionButton>
                                );
                            }
                            return (
                                <span
                                    className={
                                        hasDifferentBackground(row)
                                            ? "text-[#0b132b]"
                                            : "text-text"
                                    }
                                >
                                    --
                                </span>
                            );
                        },
                    },
                ],
            },
            {
                header: " ",
                columns: [
                    {
                        accessorKey: "note_type",
                        header: "NOTE",
                        cell: ({ row }) => (
                            <span
                                className={
                                    hasDifferentBackground(row)
                                        ? "text-[#0b132b]"
                                        : "text-text"
                                }
                            >
                                {row.original.note_type
                                    ? NoteLabels[
                                          row.original.note_type as string
                                      ]
                                    : "--"}
                            </span>
                        ),
                    },
                ],
            },
            {
                header: " ",
                columns: [
                    {
                        accessorKey: "note_author",
                        header: "CLINICIAN",
                        cell: ({ row }) => (
                            <span
                                className={
                                    hasDifferentBackground(row)
                                        ? "text-[#0b132b]"
                                        : "text-text"
                                }
                            >
                                {row.original.note_author
                                    ? displayNameInRightFormat({
                                          firstName:
                                              row.original.note_author
                                                  .first_name,
                                          lastName:
                                              row.original.note_author
                                                  .last_name,
                                      })
                                    : "--"}
                            </span>
                        ),
                    },
                ],
            },
            {
                header: " ",
                columns: [
                    {
                        accessorKey: "insurance_provider",
                        header: "INSURANCE PROVIDER",
                        cell: ({ row }) => (
                            <span
                                className={cn("uppercase text-text", {
                                    "normal-case":
                                        row.original.metadata?.self_pay &&
                                        !showInsOverSelfPay,
                                    "!text-[#0b132b]":
                                        hasDifferentBackground(row),
                                })}
                            >
                                {row.original.metadata?.self_pay &&
                                !showInsOverSelfPay
                                    ? "Self pay"
                                    : (row.original.insurance_provider &&
                                          removeEnumUnderscore(
                                              row.original.insurance_provider ||
                                                  ""
                                          )) ||
                                      "--"}
                            </span>
                        ),
                    },
                ],
            },
            {
                header: " ",
                columns: [
                    {
                        accessorKey: "interest_collected",
                        header: "Interest Payment",
                        cell: ({ row }) => (
                            <span
                                className={
                                    hasDifferentBackground(row)
                                        ? "text-[#0b132b]"
                                        : "text-text"
                                }
                            >
                                {typeof row.original.interest_collected ===
                                "number"
                                    ? showMoneyInAppFormat(
                                          row.original.interest_collected
                                      )
                                    : "--"}
                            </span>
                        ),
                    },
                ],
            },
            {
                header: "INSURANCE",
                columns: [
                    {
                        accessorKey: "insurance_charge",
                        header: "CHARGE",
                        cell: ({ row }) =>
                            showMoneyInAppFormat(row.original.insurance_amount),
                    },
                    {
                        accessorKey: "insurance_amount_collected",
                        header: "COLLECTED",
                        cell: ({ row }) =>
                            showMoneyInAppFormat(
                                row.original.insurance_amount_collected
                            ),
                    },
                ],
            },
            {
                header: "CO-INSURANCE",
                columns: [
                    {
                        accessorKey: "coinsurance_charge",
                        header: "CHARGE",
                        cell: ({ row }) =>
                            showMoneyInAppFormat(
                                row.original.coinsurance_amount
                            ),
                    },
                    {
                        accessorKey: "coinsurance_amount_collected",
                        header: "COLLECTED",
                        cell: ({ row }) =>
                            showMoneyInAppFormat(
                                row.original.coinsurance_amount_collected
                            ),
                    },
                ],
            },
            {
                header: "PR100",

                columns: [
                    {
                        accessorKey: "charge",
                        header: "CHARGE",
                        cell: ({ row }) =>
                            showMoneyInAppFormat(row.original.pr100_amount),
                    },
                    {
                        accessorKey: "collected",
                        header: "COLLECTED",
                        cell: ({ row }) =>
                            showMoneyInAppFormat(row.original.pr100_collected),
                    },
                ],
            },
            {
                accessorKey: "payment_status",
                header: "PAYMENT STATUS",
                cell: ({ row }) =>
                    row.original.payment_status ? (
                        <span>
                            {getBillPaymentStatusTag(
                                row.original.payment_status as BillPaymentStatus
                            )}
                        </span>
                    ) : (
                        "--"
                    ),
            },
        ];
    }, [showInsOverSelfPay]);

    const rowsToExpand = () => {
        const expanded: ExpandedState = {};
        data?.data?.forEach((bill, i) => {
            if (
                BillStatusesWithIssueDetails.includes(bill.invoice_status) &&
                bill.issue_category
            ) {
                expanded[i] = true;
            }
        });
        return expanded;
    };

    const rowSubComponent = ({ row }: { row: Row<IBillingResponse> }) => {
        if (!row.getIsExpanded) return null;
        return (
            <div
                className="flex text-textSec font-medium text-caption gap-0.5 pl-[--padding-card-x] my-[--padding-card-y-lg]
            items-baseline"
            >
                <span className="text-danger capitalize">
                    {removeEnumUnderscore(
                        row.original.issue_category as string
                    )}
                </span>

                {row.original.issue_notes_preview &&
                    row.original.issue_notes_preview.length && (
                        <span>
                            -{" "}
                            {row.original.issue_notes_preview.length < 150
                                ? row.original.issue_notes_preview
                                : `${row.original.issue_notes_preview?.slice(
                                      0,
                                      200
                                  )}...`}
                        </span>
                    )}
            </div>
        );
    };

    // Get bills list count
    let invoicesCount;
    if (error) invoicesCount = "";
    else if (isLoading) invoicesCount = "";
    else if (data?.data && data?.data.length === 0) invoicesCount = 0;
    else if (data?.data && data?.data.length > 0)
        invoicesCount = data.total_count;

    return (
        <>
            {showPendingReolutionDialog && (
                <Dialog open={showPendingReolutionDialog}>
                    <DialogContent
                        title={pendingResolutionDialogText}
                        handleCloseDialog={handleClosePendingResolutionDialog}
                        variant="center"
                        showFooter
                        cancelText="Cancel"
                        saveText={pendingResolutionDialogText}
                        submitBtnFormValue="pending-resolution"
                        isSubmitBtnDisabled={updateInVoiceIsMutating > 0}
                        isCancelBtnDisabled={updateInVoiceIsMutating > 0}
                    >
                        <PendingResolutionForm
                            onFormSubmit={() => {
                                handleClosePendingResolutionDialog();
                                setRowSelection({});
                            }}
                            metaProps={propsForPendingResolution || undefined}
                        />
                    </DialogContent>
                </Dialog>
            )}
            <Navbar title={`Bills(${invoicesCount})`} />
            <div className="page">
                {(!error || (error && error?.response?.status === 404)) && (
                    <>
                        <Card classNames="mb-[--spacing-header]">
                            <div className="max-w-[400px] mb-[--spacing-items]">
                                <SearchInput
                                    placeholder="Search by client or chart number"
                                    value={clientFilter}
                                    onChange={handleClientFilterChange}
                                />
                            </div>

                            <div className="flex gap-[--spacing-items] flex-wrap">
                                <Popover
                                    open={showCliniciansListPopover}
                                    onOpenChange={setShowCliniciansListPopover}
                                >
                                    <PopoverTrigger
                                        asChild
                                        onClick={() =>
                                            setShowCliniciansListPopover(true)
                                        }
                                    >
                                        <FilterButton>
                                            {selectedProvider
                                                ? truncateString(
                                                      selectedProvider.label
                                                  )
                                                : "Clinician"}
                                        </FilterButton>
                                    </PopoverTrigger>

                                    <PopoverContent
                                        className="w-[316px]"
                                        align="start"
                                    >
                                        <SearchInput
                                            placeholder="Search by clinician"
                                            onChange={(e) =>
                                                setProviderSearchString(
                                                    e.target.value
                                                )
                                            }
                                        />

                                        <div className="flex flex-col items-start max-h-[230px] overflow-y-auto">
                                            {providerList.isLoading && (
                                                <Loader />
                                            )}

                                            {providerList.error && (
                                                <ListState
                                                    isError
                                                    context="general"
                                                    stateHelperText="Please try again later"
                                                    errorMsg={
                                                        providerList.error
                                                            ?.response?.data
                                                            ?.message ||
                                                        "Error loading providers"
                                                    }
                                                    cardClass="h-[150px]"
                                                />
                                            )}

                                            {providerList.data?.data &&
                                                providerList.isSuccess && (
                                                    <>
                                                        <Button
                                                            size="auto"
                                                            variant="can-hover"
                                                            className="font-medium capitalize w-full justify-start my-1.5 shrink-0"
                                                            onClick={() => {
                                                                setProviderSearchString(
                                                                    ""
                                                                );
                                                                setSelectedProvider(
                                                                    null
                                                                );
                                                                setShowCliniciansListPopover(
                                                                    false
                                                                );
                                                            }}
                                                        >
                                                            All
                                                        </Button>

                                                        {providerList.data.data.map(
                                                            (provider) => (
                                                                <Button
                                                                    key={
                                                                        provider.provider_id
                                                                    }
                                                                    size="auto"
                                                                    variant="can-hover"
                                                                    className="font-medium capitalize w-full justify-start mb-1.5 shrink-0"
                                                                    onClick={() => {
                                                                        setSelectedProvider(
                                                                            {
                                                                                label: `${provider.provider_name}`,
                                                                                value: provider.user_id,
                                                                            }
                                                                        );
                                                                        setShowCliniciansListPopover(
                                                                            false
                                                                        );
                                                                    }}
                                                                >
                                                                    {
                                                                        provider.provider_name
                                                                    }
                                                                </Button>
                                                            )
                                                        )}
                                                    </>
                                                )}
                                        </div>
                                    </PopoverContent>
                                </Popover>
                                <InsuranceProviderFilter
                                    values={insuranceProviderIdsFilter}
                                    onChange={setInsuranceProviderIdsFilter}
                                    align="start"
                                />
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton>Bill Status</FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent align="end" width="auto">
                                        {Object.values(BillStatus).map(
                                            (status) => (
                                                <DropdownItem
                                                    key={status}
                                                    onClick={() =>
                                                        handleStatusChange(
                                                            status
                                                        )
                                                    }
                                                    isMulti
                                                    itemValue={status}
                                                    values={invoiceStatusFilter}
                                                >
                                                    {getBillStatusFilterLabel(
                                                        status
                                                    )}
                                                </DropdownItem>
                                            )
                                        )}
                                    </DropdownContent>
                                </Dropdown>

                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton>
                                            {cptCodeFilter || "CPT Code"}
                                        </FilterButton>
                                    </DropdownTrigger>

                                    <DropdownContent
                                        align="start"
                                        width="auto"
                                        className="min-w-[120px]"
                                    >
                                        <DropdownItem
                                            onClick={() =>
                                                handleCptCodeChange("")
                                            }
                                            isSelected={!cptCodeFilter}
                                        >
                                            All
                                        </DropdownItem>

                                        {Object.values(
                                            CPT_CODES.map((code) => (
                                                <DropdownItem
                                                    key={code}
                                                    onClick={() =>
                                                        handleCptCodeChange(
                                                            code
                                                        )
                                                    }
                                                    isSelected={
                                                        cptCodeFilter === code
                                                    }
                                                >
                                                    {removeEnumUnderscore(code)}
                                                </DropdownItem>
                                            ))
                                        )}
                                    </DropdownContent>
                                </Dropdown>

                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton>
                                            {" "}
                                            {pendingResolutionFilter
                                                ? truncateString(
                                                      removeEnumUnderscore(
                                                          pendingResolutionFilter
                                                      )
                                                  )
                                                : " Resolution reason"}
                                        </FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent width="auto">
                                        <DropdownItem
                                            isSelected={
                                                !pendingResolutionFilter
                                            }
                                            onClick={() =>
                                                handlePendingResolutionChange(
                                                    ""
                                                )
                                            }
                                        >
                                            All
                                        </DropdownItem>

                                        {PendingResolutionOptions.map(
                                            (reason) => (
                                                <DropdownItem
                                                    key={reason}
                                                    isSelected={
                                                        pendingResolutionFilter ===
                                                        reason
                                                    }
                                                    onClick={() =>
                                                        handlePendingResolutionChange(
                                                            reason
                                                        )
                                                    }
                                                >
                                                    {removeEnumUnderscore(
                                                        reason
                                                    )}
                                                </DropdownItem>
                                            )
                                        )}
                                    </DropdownContent>
                                </Dropdown>
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton className="normal-case">
                                            {getArchivedNotesFilterToDisplay()}
                                        </FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent width={170}>
                                        <DropdownItem
                                            isSelected={
                                                !showArchivedNotesFilter
                                            }
                                            onClick={() =>
                                                handleSetArchivedNotesFilter("")
                                            }
                                        >
                                            Show all
                                        </DropdownItem>
                                        <DropdownItem
                                            isSelected={
                                                showArchivedNotesFilter ===
                                                "true"
                                            }
                                            onClick={() =>
                                                handleSetArchivedNotesFilter(
                                                    "true"
                                                )
                                            }
                                        >
                                            Yes
                                        </DropdownItem>
                                        <DropdownItem
                                            isSelected={
                                                showArchivedNotesFilter ===
                                                "false"
                                            }
                                            onClick={() =>
                                                handleSetArchivedNotesFilter(
                                                    "false"
                                                )
                                            }
                                        >
                                            No
                                        </DropdownItem>
                                    </DropdownContent>
                                </Dropdown>

                                <Popover>
                                    <PopoverTrigger asChild>
                                        <FilterButton>Date</FilterButton>
                                    </PopoverTrigger>
                                    <PopoverContent align="end">
                                        <div className="mb-1">
                                            <DatePicker
                                                label="From"
                                                placeholderText="MM/DD/YYYY"
                                                selected={fromDateFilterToDate}
                                                onChange={
                                                    handleFromDateFilterChange
                                                }
                                                maxDate={
                                                    toDateFilterToDate ||
                                                    new Date(Date.now())
                                                }
                                                isClearable
                                            />
                                        </div>
                                        <DatePicker
                                            label="To"
                                            placeholderText="MM/DD/YYYY"
                                            selected={toDateFilterToDate}
                                            onChange={handleToDateFilterChange}
                                            minDate={fromDateFilterToDate}
                                            isClearable
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>
                            <TableFiltersDisplay
                                appliedFilters={[
                                    {
                                        key: "invoice_status[]",
                                        values: invoiceStatusFilter,
                                    },
                                    {
                                        key: "from_date",
                                        values: fromDateFilter || "",
                                    },
                                    {
                                        key: "to_date",
                                        values: toDateFilter || "",
                                    },
                                ]}
                                wrapperClass="mt-[--spacing-items]"
                            />
                        </Card>

                        {getTotalAmountOfTableRowsSelected(rowSelection)
                            .amount > 0 && (
                            <div className="flex items-center mb-[--spacing-items] gap-[--spacing-items] flex-wrap">
                                <h6 className="font-medium text-base shrink-0">
                                    {
                                        getTotalAmountOfTableRowsSelected(
                                            rowSelection
                                        ).amountString
                                    }{" "}
                                    :
                                </h6>

                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton
                                            disabled={
                                                patchInvoiceStatus.isLoading
                                            }
                                            className="bg-[var(--color-secondary)]"
                                        >
                                            Update status
                                        </FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent width={270} align="end">
                                        {sortedBillStatuses.map((status) => (
                                            <DropdownItem
                                                key={status}
                                                onClick={() =>
                                                    handleBillStatusSelect(
                                                        status
                                                    )
                                                }
                                            >
                                                {getBillStatusLabel(status)}
                                            </DropdownItem>
                                        ))}
                                    </DropdownContent>
                                </Dropdown>
                            </div>
                        )}
                    </>
                )}

                {error && error?.response?.status !== 404 && (
                    <ListState
                        isError
                        stateHelperText="Try reloading this page to solve this issue"
                        errorMsg={
                            error?.response?.data.message ||
                            `Cannot display invoices at this time please try again later`
                        }
                    />
                )}

                {data && isSuccess && data.data.length === 0 && (
                    <ListState
                        stateHelperText="Invoice list will appear here"
                        isError={false}
                        emptyMessage="There are no invoices yet"
                    />
                )}

                <div>
                    {isLoading && <Loader />}

                    {!isLoading &&
                        isSuccess &&
                        data &&
                        data.data.length > 0 && (
                            <Table
                                columns={columns}
                                data={invoiceItems}
                                rowSelection={rowSelection}
                                setRowSelection={setRowSelection}
                                handleRowClick={handleRowClick}
                                setGetSelectedData={setGetSelectedData}
                                expandedRows={rowsToExpand()}
                                renderRowSubComponent={rowSubComponent}
                                setSortConfig={setSortConfig}
                                sortConfig={sortConfig}
                                sortableColumns={sortableColumns}
                                className={styles.billslist}
                                hasPagination
                                isHeaderFixed
                                handleRowStyle={handleRowStyle}
                                pagination={
                                    <Pagination
                                        totalCount={data.total_count}
                                        totalPages={data.total_pages}
                                        currentPage={pageFilter}
                                        onPageChange={handlePageChange}
                                        limit={limitFilter}
                                        onLimitChange={handleLimitChange}
                                    />
                                }
                            />
                        )}
                </div>
            </div>
        </>
    );
}
